import { GuestModel } from 'api-hooks/guest';
import { FlightModel } from 'api-hooks/tour/tour-list/flight/model';
import { Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import { ActionType, ColumnDataType } from 'components/elements/table/type';
import Text from 'components/elements/text';
import { SeparatorVertical } from 'components/styles';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewGuest } from '../../hooks/use-get-default-values';
import { TourSaleItemGuestFormType } from '../form-type';

function tagTransformer(item: { id: string; count?: number; name: string }) {
  return {
    id: item.id,
    label: `${item.name} ${item.count ? `(X${item.count})` : ''}`,
  };
}

function useGetParticipantTableList() {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const guestColumn = useGetGuestColumn();

  return [
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 100,
      dataType: ColumnDataType.select,
      selectProps: guestColumn,
    },

    {
      header: t('common:preferences'),
      accessorKey: 'guestPreferences',
      minSize: 250,
      dataType: ColumnDataType.tags,
      tagKeyName: 'label',
      maxTags: 2,
    },
    ...deleteColumn(editable),
  ];
}

interface Props {}

export default function TourGuestCard(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const { control, getValues, trigger, clearErrors } = useFormContext();

  const tourSaleGuestsName = 'tourSales.0.tourSaleGuests' as const;

  const columns = useGetParticipantTableList();

  const { append, fields, remove, update } = useFieldArray({
    name: tourSaleGuestsName,
    control,
    keyName: '__customId',
  });

  const dispatch = React.useCallback(
    (action) => {
      const columnId = action.columnId as keyof TourSaleItemGuestFormType;
      const type = action.type as ActionType;
      const value = action.value;
      const extra = action.extra as Value;
      const currentRowIndex = action.rowIndex as number;
      const guests = getValues(tourSaleGuestsName);
      const guest = guests[currentRowIndex];

      const typeOptions = getValues('typeOptions') || [];
      const scheduleOptions = getValues('scheduleOptions') || [];
      const enumOptions = getValues('enumOptions') || [];

      const otherVal: () => Partial<TourSaleItemGuestFormType> = () => {
        if (columnId === 'guestId') {
          const _guest = extra?.[0]?.extra as GuestModel;
          const guestPreferences = [
            ..._guest.preferenceStats,
            ..._guest.preferences,
          ].map((preference) => tagTransformer(preference));
          return {
            guestName: extra?.[0]?.label as string,
            guestPreferences,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        const type =
          typeOptions.find((to) => to.extra.isDefault) ?? typeOptions?.[0];

        const flight = scheduleOptions?.[0]?.extra as FlightModel | undefined;

        const bedType = enumOptions?.[0]?.id;

        const guest = {
          ...addNewGuest(),
          bedType,
          flightName: flight?.name || '',
          tourFlightId: flight?.id || '',
          tourFlightRefId: flight?.refId || '',
          price: type?.extra?.price || undefined,
          tourPriceId: type?.extra?.id || '',
          type: type?.extra?.label || '',
          tourPriceRefId: type?.extra?.refId || '',
        } as TourSaleItemGuestFormType;

        append(guest);
        trigger(tourSaleGuestsName);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(tourSaleGuestsName);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...guest,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(tourSaleGuestsName);
      }
    },
    [append, clearErrors, getValues, remove, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <Text variant="HeadingXSmall">{t('common:tour_participant')}</Text>
      <SeparatorVertical $margin={16} />
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={tourSaleGuestsName}
        uniqueRowKey="customId"
      />
    </div>
  );
}
